/**
 * The machine-directed blocks Benny embeds in a reply, and the pure helpers
 * that get them back out.
 *
 * Benny answers in markdown, but some of what it says is addressed to the app
 * rather than to the reader: ```scrambl-cards``` (records to surface),
 * ```scrambl-followups``` (composer chips), ```scrambl-canvas``` (canvas
 * directives), ```scrambl-view``` (live ScQL panes) and `scrambl://` links.
 * Every client has to strip and parse these the SAME way or the same reply
 * reads differently on web than it does on a phone — hence one copy, here.
 *
 * Pure string work only: no DOM, no React, no Node. Safe under Hermes.
 */

import type {
  CanvasCardRef,
  CanvasDirective,
  ComposedViewSpec,
  ScramblCard,
  ScramblLinkTarget,
} from '../types/assistant'

// ─── Fence patterns ──────────────────────────────────────────────────────────
//
// All four capture their body so the same constant serves both `matchAll`
// (parse the body) and `replace` (drop the block). They are `/g`, so treat
// them as read-only inputs to `matchAll`/`replace` — never `.test()`, which
// would carry `lastIndex` between calls.

export const SCRAMBL_CARDS_FENCE = /```scrambl-cards\s*([\s\S]*?)```/g
export const SCRAMBL_FOLLOWUPS_FENCE = /```scrambl-followups\s*([\s\S]*?)```/g
export const SCRAMBL_CANVAS_FENCE = /```scrambl-canvas\s*([\s\S]*?)```/g
export const SCRAMBL_VIEW_FENCE = /```scrambl-view\s*([\s\S]*?)```/g

/** A markdown link whose href is one of our own schemes. */
export const SCRAMBL_LINK_MARKDOWN =
  /\[([^\]]+)\]\(scrambl(?:-help)?:\/\/[^)]*\)/g

// ─── Card identity ───────────────────────────────────────────────────────────

/** The canvas key for one record: `type:id`, type lowercased. */
export function cardKey(type: string, id: string): string {
  return `${type.toLowerCase()}:${id}`
}

// ─── Status labels ───────────────────────────────────────────────────────────

const STATUS_TYPE_LABELS: Record<string, string> = {
  NOT_STARTED: 'To Do',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Done',
  ON_HOLD: 'On Hold',
  CANCELED: 'Canceled',
  CANCELLED: 'Canceled',
}

/**
 * Human label for a card's status. Known status TYPES get their product
 * wording; anything else is title-cased as written, so a custom status keeps
 * the name the org gave it.
 */
export function formatAssistantStatusLabel(status?: string): string | null {
  if (!status) return null
  const trimmed = status.trim()
  if (!trimmed) return null
  const upper = trimmed.toUpperCase()
  if (STATUS_TYPE_LABELS[upper]) return STATUS_TYPE_LABELS[upper] as string
  return trimmed
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

// ─── Cards ───────────────────────────────────────────────────────────────────

/**
 * Parse one ```scrambl-cards``` body. Returns null on anything unparseable so
 * the caller renders nothing rather than raw JSON.
 */
export function parseScramblCards(jsonStr: string): ScramblCard[] | null {
  try {
    const cleaned = jsonStr.replace(/^\s+|\s+$/g, '')
    const parsed = JSON.parse(cleaned)
    if (!Array.isArray(parsed)) return null
    return parsed
      .filter(
        (item: any) =>
          item &&
          typeof item.type === 'string' &&
          typeof item.id === 'string' &&
          typeof item.name === 'string',
      )
      .map((item: any) => ({
        ...item,
        statusName:
          item.statusName ??
          item.statusLabel ??
          formatAssistantStatusLabel(item.status ?? item.statusId),
        statusType: item.statusType ?? item.statusTypeId,
      })) as ScramblCard[]
  } catch {
    return null
  }
}

const APPROVAL_CONTEXT_PATTERN =
  /\b(?:approvals?|awaiting approval|waiting for (?:your )?approval|review(?:ing)? timesheets?)\b/i

/**
 * Older Benny responses used the generic `time_entry`/`timesheet` type even
 * when the surrounding response was an approver queue. Normalize those
 * already-cached messages so they use the admin Timesheets destination while
 * leaving genuinely personal time-entry conversations unchanged.
 */
export function normalizeAssistantCardsForContext(
  cards: ScramblCard[],
  surroundingText: string,
): ScramblCard[] {
  if (!APPROVAL_CONTEXT_PATTERN.test(surroundingText)) return cards

  return cards.map(card => {
    const type = card.type.toLowerCase()
    if (type !== 'time_entry' && type !== 'timesheet') return card
    return { ...card, type: 'timesheet_approval' }
  })
}

// ─── Followups ───────────────────────────────────────────────────────────────

/**
 * Parse a `scrambl-followups` JSON block. Expects an array of 2-3 short
 * strings the user might want to send next. Returns null on any parse failure
 * so the caller can fall back to rendering nothing.
 */
export function parseScramblFollowups(jsonStr: string): string[] | null {
  try {
    const cleaned = jsonStr.replace(/^\s+|\s+$/g, '')
    const parsed = JSON.parse(cleaned)
    if (!Array.isArray(parsed)) return null
    const strings = parsed
      .filter((item: unknown) => typeof item === 'string')
      .map((s: string) => s.trim())
      .filter((s: string) => s.length > 0 && s.length <= 80)
    if (strings.length === 0) return null
    return strings.slice(0, 4)
  } catch {
    return null
  }
}

// ─── Canvas directives ───────────────────────────────────────────────────────

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

/**
 * A page path Benny may embed: same-app relative, one leading slash, no
 * protocol/host smuggling. Anything else is dropped rather than rendered.
 */
const SAFE_PAGE_PATH = /^\/(?!\/)[A-Za-z0-9\-_/.]*(?:\?[A-Za-z0-9\-_=&%]*)?$/

function pageRef(value: unknown): CanvasDirective['page'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const ref = value as Record<string, unknown>
  const name = typeof ref.name === 'string' ? ref.name.toLowerCase() : undefined
  const path =
    typeof ref.path === 'string' && SAFE_PAGE_PATH.test(ref.path)
      ? ref.path
      : undefined
  if (!name && !path) return null
  return {
    ...(name ? { name } : {}),
    ...(path ? { path } : {}),
    ...(typeof ref.title === 'string' ? { title: ref.title } : {}),
  }
}

function peekRef(value: unknown): CanvasDirective['peek'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const ref = value as Record<string, unknown>
  if (typeof ref.type !== 'string' || typeof ref.id !== 'string') return null
  return {
    type: ref.type.toLowerCase(),
    id: ref.id,
    ...(typeof ref.name === 'string' ? { name: ref.name } : {}),
  }
}

function refArray(value: unknown): CanvasCardRef[] {
  if (!Array.isArray(value)) return []
  return value.filter(
    (item): item is CanvasCardRef =>
      !!item &&
      typeof (item as CanvasCardRef).type === 'string' &&
      typeof (item as CanvasCardRef).id === 'string',
  )
}

/** Parse every complete ```scrambl-canvas``` fence in a message, in order. */
export function parseScramblCanvasDirectives(
  content: string,
): CanvasDirective[] {
  const directives: CanvasDirective[] = []
  for (const match of content.matchAll(SCRAMBL_CANVAS_FENCE)) {
    try {
      const parsed = JSON.parse((match[1] ?? '').trim()) as Record<
        string,
        unknown
      >
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        continue
      }
      directives.push({
        pin: refArray(parsed.pin),
        unpin: refArray(parsed.unpin),
        hide: refArray(parsed.hide),
        show: refArray(parsed.show),
        focus:
          typeof parsed.focus === 'string'
            ? parsed.focus.toLowerCase()
            : parsed.focus === null
              ? null
              : undefined,
        order: Array.isArray(parsed.order)
          ? stringArray(parsed.order).map(type => type.toLowerCase())
          : undefined,
        reset: parsed.reset === true,
        peek: peekRef(parsed.peek),
        page: pageRef(parsed.page),
      })
    } catch {
      // Malformed directive — ignore rather than break the canvas.
    }
  }
  return directives
}

// ─── Composed views ──────────────────────────────────────────────────────────

/** Modules with a ScQL field registry — anything else can't be executed. */
const COMPOSED_VIEW_MODULES = new Set(['TASK', 'TICKET', 'PROJECT', 'CONTACT'])

/** Parse one ```scrambl-view``` body; null when it can't be trusted. */
export function parseComposedView(
  id: string,
  body: string,
): ComposedViewSpec | null {
  let parsed: unknown
  try {
    parsed = JSON.parse(body.trim())
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null
  }
  const value = parsed as Record<string, unknown>

  const title = typeof value.title === 'string' ? value.title.trim() : ''
  const query = typeof value.query === 'string' ? value.query.trim() : ''
  if (!title || !query) return null

  const requested = stringArray(value.modules).map(module =>
    module.toUpperCase(),
  )
  const modules = requested.filter(module =>
    COMPOSED_VIEW_MODULES.has(module),
  )
  if (modules.length === 0) {
    // Nothing queryable — but if the model NAMED modules, surface the
    // rejection as a stub pane rather than vanishing the view.
    const unsupported = requested.filter(
      module => !COMPOSED_VIEW_MODULES.has(module),
    )
    if (unsupported.length === 0) return null
    return { id, title, query, modules: [], unsupportedModules: unsupported }
  }

  const limit =
    typeof value.limit === 'number' &&
    Number.isFinite(value.limit) &&
    value.limit >= 1 &&
    value.limit <= 50
      ? Math.floor(value.limit)
      : undefined

  return { id, title, query, modules, limit }
}

/**
 * Every valid ```scrambl-view``` in ONE message, in order. Ids stay tied to
 * the fence's position in the message (invalid fences still consume an index)
 * so a spec keeps its identity as the message re-renders.
 */
export function parseScramblViews(
  content: string,
  messageId: string,
): ComposedViewSpec[] {
  const views: ComposedViewSpec[] = []
  let index = 0
  for (const match of content.matchAll(SCRAMBL_VIEW_FENCE)) {
    const view = parseComposedView(`${messageId}:${index}`, match[1] ?? '')
    index += 1
    if (view) views.push(view)
  }
  return views
}

// ─── Deep links ──────────────────────────────────────────────────────────────

/** `scrambl://type/uuid` → its target. Null for anything else. */
export function parseScramblLink(href: string): ScramblLinkTarget | null {
  if (!href.startsWith('scrambl://')) return null
  try {
    const rest = href.slice('scrambl://'.length)
    const slashIndex = rest.indexOf('/')
    if (slashIndex === -1) return null
    const type = rest.slice(0, slashIndex)
    const id = rest.slice(slashIndex + 1).split('?')[0]
    if (!type || !id) return null
    return { type, id }
  } catch {
    return null
  }
}

// ─── Stripping ───────────────────────────────────────────────────────────────

/**
 * While a response is still streaming, an unterminated ```scrambl-… fence
 * would dump raw JSON into the bubble until its closing fence arrives.
 * Truncate the visible content at such a fence; completed blocks are left
 * alone (the markdown renderer turns them into cards/chips as they finish).
 */
export function sanitizeStreamingContent(content: string): string {
  const fences = [...content.matchAll(/^```[^\n]*/gm)]
  if (fences.length % 2 === 0) return content

  const last = fences[fences.length - 1]
  if (!last) return content
  const info = last[0].slice(3).trim().toLowerCase()
  // Hide the block even while its name is still streaming in ("scr",
  // "scrambl-car", …). Ordinary code fences keep streaming visibly.
  const isScramblBlock =
    info.length > 0 &&
    ('scrambl-cards'.startsWith(info) ||
      'scrambl-followups'.startsWith(info) ||
      info.startsWith('scrambl'))
  if (!isScramblBlock) return content

  return content.slice(0, last.index).trimEnd()
}

/**
 * What a thread bubble shows when the machine blocks render elsewhere: cards
 * on the canvas, followups under the composer, directives already applied.
 * Streaming-safe — a half-written fence is truncated rather than dumped.
 */
export function stripAssistantFences(content: string): string {
  return sanitizeStreamingContent(content)
    .replace(SCRAMBL_CARDS_FENCE, '')
    .replace(SCRAMBL_FOLLOWUPS_FENCE, '')
    .replace(SCRAMBL_CANVAS_FENCE, '')
    .replace(SCRAMBL_VIEW_FENCE, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Convert an assistant message to clean plain text for the clipboard:
 * card blocks become a readable bullet list, followup chips are dropped,
 * and scrambl:// deep links keep just their label.
 */
export function toClipboardText(content: string): string {
  // A trailing unterminated machine block (aborted stream) is noise either way.
  let result = sanitizeStreamingContent(content)

  result = result.replace(SCRAMBL_CARDS_FENCE, (_match, body: string) => {
    try {
      const cards = JSON.parse(body.trim()) as ScramblCard[]
      if (Array.isArray(cards)) {
        return cards
          .map(
            card =>
              `- ${[card.identifier, card.name].filter(Boolean).join(' — ')}`,
          )
          .join('\n')
      }
    } catch {
      // Unparseable machine block — better to drop it than paste JSON.
    }
    return ''
  })

  result = result.replace(SCRAMBL_FOLLOWUPS_FENCE, '')
  result = result.replace(SCRAMBL_CANVAS_FENCE, '')
  result = result.replace(SCRAMBL_VIEW_FENCE, '')
  result = result.replace(SCRAMBL_LINK_MARKDOWN, '$1')

  return result.replace(/\n{3,}/g, '\n\n').trim()
}
